import { array, boolean, mixed, object, ObjectSchema, Schema, string, StringSchema } from 'yup';

import { FormErrorMessage } from '~/constants/messages';
import { HOUR_OPTIONS, MINUTE_OPTIONS } from '~/constants/organization_business_hour';
import { onlineQualificationConfirmationUrlRegexp } from '~/constants/regexp';

import { Fields } from './types';

export const DESCRIPTION_MAX_LENGTH = 300;
export const OPENINGHOUR_MAX_LENGTH = 300;

export const validationSchema: ObjectSchema<Fields> = object({
  published: string()
    .oneOf(['publish', 'unpublish'])
    .required()
    .test(
      'published',
      '「オンライン服薬指導」または「処方箋ネット受付」を有効にしてください',
      function (published) {
        if (published === 'unpublish') return true;

        return this.parent.telemedicine === 'enabled' || this.parent.facetoface === 'enabled';
      },
    ),
  telemedicine: string().oneOf(['enabled', 'disabled']).required(),
  facetoface: string().oneOf(['enabled', 'disabled']).required(),
  description: string().when(
    'published',
    ([published]: 'publish'[] | 'unpublish'[], schema: StringSchema) => {
      return published === 'publish'
        ? schema
            .label('紹介文')
            .required(FormErrorMessage.required)
            .max(DESCRIPTION_MAX_LENGTH, FormErrorMessage.maxLengthRequired)
        : schema
            .label('紹介文')
            .notRequired()
            .max(DESCRIPTION_MAX_LENGTH, FormErrorMessage.maxLengthRequired);
    },
  ),
  website: string().label('URL').url(FormErrorMessage.invalid).notRequired() as StringSchema,
  openingHours: string()
    .label('営業時間')
    .notRequired()
    .max(OPENINGHOUR_MAX_LENGTH, FormErrorMessage.maxLengthRequired) as StringSchema,
  logo: mixed().notRequired() as Schema<File>,
  onlineQualificationConfirmation: string().oneOf(['enabled', 'disabled']).required(),
  onlineQualificationConfirmationUrl: string().when(
    'onlineQualificationConfirmation',
    ([onlineQualificationConfirmation]: 'enabled'[] | 'disabled'[], schema: StringSchema) => {
      return onlineQualificationConfirmation === 'enabled'
        ? schema
            .label('マイナ在宅受付Web URL')
            .matches(onlineQualificationConfirmationUrlRegexp, {
              message: FormErrorMessage.onlineQualificationConfirmationUrlInvalid,
              excludeEmptyString: true,
            })
            .required(FormErrorMessage.required)
        : schema
            .label('マイナ在宅受付Web URL')
            .notRequired()
            .matches(onlineQualificationConfirmationUrlRegexp, {
              message: FormErrorMessage.onlineQualificationConfirmationUrlInvalid,
              excludeEmptyString: true,
            });
    },
  ),
  businessHours: array()
    .of(
      object({
        dayType: string().required(),
        enabled: boolean().required(),
        timeRanges: array()
          .of(
            object({
              organizationBusinessHourId: string().optional() as StringSchema<string | undefined>,
              startHour: string().oneOf(HOUR_OPTIONS).default('--'),
              startMinute: string().oneOf(MINUTE_OPTIONS).default('--'),
              endHour: string().oneOf(HOUR_OPTIONS).default('--'),
              endMinute: string().oneOf(MINUTE_OPTIONS).default('--'),
            }),
          )
          .required(),
      }),
    )
    .required()
    .test('businessHours', '営業時間を設定してください', function (businessHours) {
      const published = this.parent.published;
      if (published === 'unpublish') return true;

      const enabledDays = businessHours?.filter((day) => day.enabled) ?? [];
      if (enabledDays.length === 0) {
        return false;
      }

      const hasInvalidTime = enabledDays.some((day) => {
        return !day.timeRanges.some(
          (range) =>
            range.startHour !== '--' &&
            range.startMinute !== '--' &&
            range.endHour !== '--' &&
            range.endMinute !== '--',
        );
      });
      if (hasInvalidTime) {
        return false;
      }

      return true;
    }),
});
